import { Box, Stack } from '@n26/nxd'
import { useMemo } from 'react'
import { Text } from '@components/Text'
import { FooterNavigationItems } from '@components/Footer/FooterNavigationItems'
import { FooterStoreIcon } from '@components/Footer/FooterStoreIcon'
import type { AccordionGroupProps } from '@components/Accordion'
import { Accordion } from '@components/Accordion'
import type {
  GetFooterType as FooterDataProps,
  NavCluster,
  NavItems,
} from '@helpers/getFooter'
import type { FooterAdFragmentFragment } from '@customTypes/generated/contentfulGraphqlAPI'
import { getMobileMenuData } from '@helpers/getMobileMenuData'
import { useGetLocale } from '@hooks/useGetLocale'
import { getDataFromReadMore } from '@helpers/getDataFromReadMore'
import { Link } from '@components/Link'
import { ResponsiveImage } from '@components/ResponsiveImage'

export const MobileFooterNavigationGroup = ({
  upperMenu,
  lowerMenu,
  footerAdData,
}: {
  upperMenu: Array<AccordionGroupProps> | undefined
  lowerMenu: Array<AccordionGroupProps> | undefined
  footerAdData?: FooterAdProps
}) => {
  return (
    <Stack>
      {upperMenu && (
        <Box paddingX='16' paddingY='12'>
          <Accordion
            triggerTextVariant='standard-bold'
            showBorders
            items={upperMenu}
            trackingEvent='footer click'
          />
        </Box>
      )}
      {lowerMenu && (
        <Box padding='16'>
          <Accordion
            triggerTextVariant='standard-bold'
            showBorders
            items={lowerMenu}
            trackingEvent='footer click'
          />
        </Box>
      )}
      {footerAdData && <FooterAd {...footerAdData} />}
    </Stack>
  )
}

type FooterAdProps = Omit<FooterAdFragmentFragment, 'type'>

export const FooterAd = ({
  heading,
  subtitle,
  media,
  appStoreLogoAsset,
  googlePlayLogoAsset,
}: FooterAdProps) => {
  const mediaUrl = media?.image?.url
  const altText = media?.alt || ''

  return (
    <Box
      display='flex'
      alignItems='flex-end'
      className='md:max-w-xl flex-col md:flex-row lg:flex-col items-center justify-self-center lg:items-center gap-16'
    >
      <Box className='p-16 md:p-0 md:pb-32 max-w-xs'>
        <Box gap='16' display='flex' flexDirection='column' textAlign='center'>
          <Text variant='standard-bold'>{heading}</Text>
          <Text variant='small'>{subtitle}</Text>
          <Box
            display='flex'
            flexDirection='row'
            gap='16'
            justifyContent='center'
          >
            <FooterStoreIcon image={appStoreLogoAsset} platform='ios' />
            <FooterStoreIcon image={googlePlayLogoAsset} platform='android' />
          </Box>
        </Box>
      </Box>
      <Box>
        {mediaUrl && (
          <ResponsiveImage
            className='mx-auto'
            sources={[{ breakpoint: 0, src: mediaUrl, width: 280 }]}
            alt={altText}
            originalWidth={media.image?.width}
            originalHeight={media.image?.height}
          />
        )}
      </Box>
    </Box>
  )
}

export type FooterRowProps = {
  columnsData: FooterDataProps['mainFooter']['firstRow']
  columnsStyles: {
    firstColumnStyles: string
    secondColumnStyles: string
  }
  rowStyles: string
}

type FooterColumnProps = {
  columnData: NonNullable<
    NonNullable<FooterRowProps['columnsData']>['firstColumn']
  >
  columnStyles: string
}

type FooterItemProps = {
  footerItemsData: NavItems
  inCluster?: boolean
}

const FooterItems = ({
  footerItemsData,
  inCluster = false,
}: FooterItemProps) => {
  const { name, navigationItems, readMore } = footerItemsData

  const locale = useGetLocale()

  if (!locale) return null

  const readMoreData = getDataFromReadMore(locale, readMore)
  const footerLinkStyles =
    'hover:text-teal text-neutral-alternate text-14 font-regular mb-16'

  return (
    <div>
      {name && (
        <>
          {inCluster ? (
            <div className='mb-12'>
              <Text as='h6' variant='small-bold'>
                {name}
              </Text>
            </div>
          ) : (
            <div className='pb-32'>
              <Text as='h6' variant='standard-bold'>
                {name}
              </Text>
            </div>
          )}
        </>
      )}
      <div className='flex flex-col'>
        <FooterNavigationItems
          linkStyles={footerLinkStyles}
          navigationItems={navigationItems}
          groupName={name}
        />
      </div>
      {readMoreData && (
        <Link className='text-teal text-14 font-medium' url={readMoreData.href}>
          {readMoreData?.label}
        </Link>
      )}
    </div>
  )
}

type FooterClusterProps = { footerClusterData: NavCluster }

const FooterCluster = (props: FooterClusterProps) => {
  const { footerClusterData } = props
  const { name, navigationCluster } = footerClusterData

  return (
    <div>
      {name && (
        <div className='pb-32'>
          <Text variant='standard-bold'>{name}</Text>
        </div>
      )}
      <div
        className={`grid grid-cols-${navigationCluster.length} max-w-[50%] lg:max-w-full`}
      >
        {navigationCluster.reduce<JSX.Element[]>(
          (clusterItems, clusterItem, index) => {
            const { name } = clusterItem
            clusterItems.push(
              <FooterItems
                key={name || `${index}-cluster-item`}
                footerItemsData={clusterItem}
                inCluster
              />
            )

            return clusterItems
          },
          []
        )}
      </div>
    </div>
  )
}

const FooterColumn = ({ columnData, columnStyles }: FooterColumnProps) => {
  return (
    <div
      className={`grid grid-cols-${columnData.length} gap-x-16 ${columnStyles} flex-grow`}
    >
      {columnData.reduce<JSX.Element[]>((columnItems, columnItem, index) => {
        if (columnItem.type === 'FooterAd') {
          const { type, heading, ...rest } = columnItem
          columnItems.push(
            <FooterAd
              key={heading || `${index}-column-item`}
              heading={heading}
              {...rest}
            />
          )
        } else if (columnItem.type === 'navItem') {
          const { name } = columnItem
          columnItems.push(
            <FooterItems
              key={name || `${index}-column-item`}
              footerItemsData={columnItem}
            />
          )
        } else if (columnItem.type === 'navCluster') {
          const { name } = columnItem
          columnItems.push(
            <FooterCluster
              key={name || `${index}-column-item`}
              footerClusterData={columnItem}
            />
          )
        }

        return columnItems
      }, [])}
    </div>
  )
}

const FooterRow = ({
  columnsData,
  columnsStyles,
  rowStyles,
}: FooterRowProps) => {
  if (!columnsData) return null
  const { firstColumn, secondColumn } = columnsData
  const { firstColumnStyles, secondColumnStyles } = columnsStyles

  return (
    <div className='border-t  border-t-neutral pt-32 border-teal'>
      <div
        className={`flex flex-col lg:flex-row max-w-full m-auto gap-16 px-32 ${rowStyles}`}
      >
        {firstColumn && (
          <FooterColumn
            columnData={firstColumn}
            columnStyles={firstColumnStyles}
          />
        )}
        {secondColumn && (
          <FooterColumn
            columnData={secondColumn}
            columnStyles={secondColumnStyles}
          />
        )}
      </div>
    </div>
  )
}

export const FooterNavigationGroup = ({
  data,
}: {
  data: FooterDataProps['mainFooter']
}) => {
  const { firstRow, secondRow } = data
  const locale = useGetLocale()
  const upperMenu = useMemo(
    () => getMobileMenuData(firstRow, locale),
    [firstRow, locale]
  )
  const lowerMenu = useMemo(
    () => getMobileMenuData(secondRow, locale),
    [secondRow, locale]
  )

  const footerAdData = useMemo(() => {
    if (upperMenu && lowerMenu) {
      if (upperMenu.footerAdData) return upperMenu.footerAdData
      if (lowerMenu.footerAdData) return lowerMenu.footerAdData
    }
  }, [upperMenu, lowerMenu])

  if (!locale) return null

  return (
    <>
      <div className='md:hidden'>
        {(upperMenu?.columnData || lowerMenu?.columnData) && (
          <MobileFooterNavigationGroup
            upperMenu={upperMenu?.columnData}
            lowerMenu={lowerMenu?.columnData}
            footerAdData={footerAdData}
          />
        )}
      </div>
      <div className='hidden md:block max-w-container mx-auto'>
        {firstRow && (
          <FooterRow
            columnsData={{
              firstColumn: firstRow.firstColumn,
              secondColumn: firstRow.secondColumn,
            }}
            rowStyles=' md:auto-rows-min md:gap-y-48 lg:grid-cols-10 lg:grid-rows-1'
            columnsStyles={{
              firstColumnStyles: 'lg:col-span-4',
              secondColumnStyles: 'lg:col-span-6',
            }}
          />
        )}
        {secondRow && (
          <FooterRow
            columnsData={{
              firstColumn: secondRow.firstColumn,
              secondColumn: secondRow.secondColumn,
            }}
            rowStyles=' md:auto-rows-min md:gap-y-48 lg:grid-cols-9 lg:grid-rows-1'
            columnsStyles={{
              firstColumnStyles: 'lg:col-span-4',
              secondColumnStyles: 'lg:col-span-5',
            }}
          />
        )}
      </div>
    </>
  )
}
